/**
 * Centralized copy for the "convert free user to paid" CTAs.
 *
 * Users are offered a one-time free trial (see the backend's
 * `isUserEligibleForFreeTrial`). While a user is still eligible we lead with
 * "Start Free Trial"; once they've used their trial (or already subscribed) the
 * same CTAs revert to the existing "Upgrade" wording.
 *
 * `isTrialEligible` comes from `selectIsTrialEligible`, which defaults to
 * `false` when the backend hasn't supplied the flag — so every CTA safely falls
 * back to the "Upgrade" wording if the field is missing.
 */

/** Compact nav / primary CTA label (e.g. sidebar "Upgrade Now" button). */
export const getUpgradeCtaLabel = (isTrialEligible: boolean): string =>
  isTrialEligible ? 'Start Free Trial' : 'Upgrade Now';

/** Daily-limit modal primary action. */
export const getUnlimitedAccessCtaLabel = (isTrialEligible: boolean): string =>
  isTrialEligible
    ? 'Start Free Trial for Unlimited Access'
    : 'Upgrade for Unlimited Access';

/** Inline AI-chat unlock link/label. */
export const getChatUnlockCtaLabel = (isTrialEligible: boolean): string =>
  isTrialEligible
    ? 'Start Free Trial to unlock AI Chat'
    : 'Upgrade to unlock AI Chat';

/**
 * Tooltip shown on the gated Timed Mode toggle.
 *
 * @param withPricingHint - Appends " Click to view pricing." (used where the
 *   tooltip sits on a button that navigates to pricing).
 */
export const getTimedModeUpgradeTooltip = (
  isTrialEligible: boolean,
  withPricingHint = false
): string => {
  const base = isTrialEligible
    ? 'Start your free trial to access Timed Mode.'
    : 'Upgrade to access Timed Mode.';

  return withPricingHint ? `${base} Click to view pricing.` : base;
};
